import React from 'react';
import classNames from 'classnames';
import { SelectInput } from './SelectInput';
import { TextInput } from './TextInput';

interface MobileInputProps {
	label: string;
	required?: boolean;
	countryValue?: any;
	onCountryChange?: (val: any) => void;
	phoneValue?: string;
	onPhoneChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	error?: string;
	disabled?: boolean;
}

export const MobileInput: React.FC<MobileInputProps> = ({
	label,
	required,
	countryValue,
	onCountryChange,
	phoneValue,
	onPhoneChange,
	error,
	disabled,
}) => {
	return (
		<div className="flex flex-col gap-1 w-full">
			<label className={classNames('text-sm font-medium', disabled ? 'text-gray-400' : 'text-gray-700')}>
				{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
			</label>
			<div className="flex">
				<div className="w-24">
					<SelectInput
						label=""
						value={countryValue}
						onChange={onCountryChange}
						options={[{ value: '+61', label: '+61' }]}
						className="!gap-0"
						isDisabled={disabled}
						styles={{
							control: (base, state) => ({
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
								borderRight: 0,
								backgroundColor: disabled ? '#f3f4f6' : '#f9fafb',
								borderColor: '#d1d5db',
								borderWidth: '1px',
								borderStyle: 'solid',
							}),
						}}
					/>
				</div>
				<div className="flex-1">
					<TextInput
						value={phoneValue}
						onChange={onPhoneChange}
						className="rounded-l-none"
						id="mobile-number"
						disabled={disabled}
					/>
				</div>
			</div>
			{error && <span className="text-xs text-red-500 mt-0.5">{error}</span>}
		</div>
	);
};

interface MultiPartInputProps {
	label: string;
	required?: boolean;
	tooltip?: string;
	/**
	 * Rendered at the right-hand end of the label row. For a note that belongs with the field but
	 * must not add a line beneath it — anything below the control changes the row's height, and in a
	 * grid of inputs that pushes everything beside it out of line as soon as the note appears.
	 */
	labelExtra?: React.ReactNode;
	parts: {
		type: 'select' | 'text';
		width?: string;
		props: any;
		key: string;
	}[];
	disabled?: boolean;
}

export const MultiPartInput: React.FC<MultiPartInputProps> = ({
	label,
	required,
	tooltip,
	labelExtra,
	parts,
	disabled,
}) => {
	return (
		<div className="flex flex-col gap-1 w-full">
			<div className="flex items-center gap-1">
				<label className={classNames('text-sm', disabled ? 'text-gray-400' : 'text-gray-800')}>
					{label} {required && <span className="text-[var(--colour-brand-primary)]">*</span>}
				</label>
				{tooltip && (
					<div className="relative group">
						<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
							help
						</span>
						<div
							className={classNames(
								'absolute left-1/2 -translate-x-1/2 bottom-full mb-2',
								'hidden group-hover:block w-64 px-3 py-2',
								'text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none',
							)}
						>
							{tooltip}
						</div>
					</div>
				)}
				{labelExtra && <div className="ml-auto">{labelExtra}</div>}
			</div>
			<div
				className={classNames(
					'flex border rounded-md overflow-hidden h-[42px]',
					disabled ? 'border-gray-300 bg-gray-50' : 'border-gray-300',
				)}
			>
				{parts.map((part, index) => (
					<div
						key={part.key}
						data-test-part={part.key}
						className={classNames(
							'flex items-center',
							part.width || 'flex-1',
							index < parts.length - 1 &&
								(disabled ? 'border-r border-gray-200' : 'border-r border-gray-300'),
						)}
					>
						{part.type === 'select' ? (
							<SelectInput
								label=""
								{...part.props}
								isDisabled={disabled || part.props.isDisabled}
								className="w-full !gap-0"
								styles={{
									control: base => ({
										...base,
										border: 'none',
										borderRadius: 0,
										minHeight: '40px',
										backgroundColor: disabled || part.props.isDisabled ? '#f3f4f6' : 'transparent',
									}),
									indicatorSeparator: () => ({ display: 'none' }),
								}}
							/>
						) : (
							<TextInput
								label=""
								{...part.props}
								disabled={disabled || part.props.disabled}
								className="!border-none !rounded-none !h-full w-full"
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
